import { useState, useEffect } from "react"
import { message, Spin, Card, Form } from "antd"
import { useUserStore } from "@/stores/user/userStore"
import { useSapSupplierStore } from "@/stores/sap-supplier/sapSupplierStore"
import { useNavigate } from "react-router"
import { TempLayout } from "@/layouts"
import SapSupplierForm, { AttachmentItem } from "./components/SapSupplierForm"
import { confirmSapImportSubmit } from "../shared/utils/confirmSapImportSubmit"
import { EMPTY_SAP_IMPORT_VALIDATION, extractSapImportValidationFromError, normalizeSapImportValidation } from "../shared/utils/sapImportValidation"
import { ISapImportValidationError } from "@/types"


const SapSuppliersAdd = () => {
  const navigate = useNavigate()
  const { userData } = useUserStore()
  const [form] = Form.useForm();
  const [file, setFile] = useState<File | null>(null)
  const [attachmentItems, setAttachmentItems] = useState<AttachmentItem[]>([])
  const [loading, setLoading] = useState(false)
  const [previewModalOpen, setPreviewModalOpen] = useState(false)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [previewColumns, setPreviewColumns] = useState<any[]>([])
  const [previewItems, setPreviewItems] = useState<any[]>([])
  const [previewIsValid, setPreviewIsValid] = useState(true)
  const [previewRequiredFields, setPreviewRequiredFields] = useState<string[]>([])
  const [previewValidationErrors, setPreviewValidationErrors] = useState<ISapImportValidationError[]>([])
  const [canSubmit, setCanSubmit] = useState(false)
  const { createSapSupplier, previewSupplierFile } = useSapSupplierStore()

  const handleFileChange = async (info: any) => {
    if (info.file.status === "removed") {
      setFile(null);
      setPreviewColumns([]);
      setPreviewItems([]);
      setPreviewError(null);
      setPreviewIsValid(EMPTY_SAP_IMPORT_VALIDATION.isValid);
      setPreviewRequiredFields(EMPTY_SAP_IMPORT_VALIDATION.requiredFields);
      setPreviewValidationErrors(EMPTY_SAP_IMPORT_VALIDATION.validationErrors);
      setPreviewModalOpen(false);
      form.setFieldValue('file', undefined);
      return;
    }

    let fileObj: File | undefined;

    if (info.file instanceof File) {
      fileObj = info.file;
    }
    else if (info.file.originFileObj) {
      fileObj = info.file.originFileObj;
    }
    else if (info.fileList && info.fileList[0]) {
      if (info.fileList[0] instanceof File) {
        fileObj = info.fileList[0];
      } else if (info.fileList[0].originFileObj) {
        fileObj = info.fileList[0].originFileObj;
      }
    }

    if (fileObj instanceof File) {
      setFile(fileObj);
      setPreviewError(null);
      setPreviewLoading(true);
      form.setFieldValue('file', fileObj);

      try {
        const res = await previewSupplierFile(fileObj);
        const validationState = normalizeSapImportValidation(res?.data);
        setPreviewColumns(res?.data?.columns || []);
        setPreviewItems(res?.data?.items || []);
        setPreviewIsValid(validationState.isValid);
        setPreviewRequiredFields(validationState.requiredFields);
        setPreviewValidationErrors(validationState.validationErrors);
        setPreviewModalOpen(true);
      } catch (err: any) {
        setPreviewError(err?.response?.data?.message || "Gagal preview file");
        setPreviewColumns([]);
        setPreviewItems([]);
        setPreviewIsValid(EMPTY_SAP_IMPORT_VALIDATION.isValid);
        setPreviewRequiredFields(EMPTY_SAP_IMPORT_VALIDATION.requiredFields);
        setPreviewValidationErrors(EMPTY_SAP_IMPORT_VALIDATION.validationErrors);
        setPreviewModalOpen(true);
      } finally {
        setPreviewLoading(false);
      }
    }
  };

  const handleAddAttachment = () => {
    if (attachmentItems.length < 5) {
      setAttachmentItems([...attachmentItems, { file: null, description: "" }]);
    }
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachmentItems(attachmentItems.filter((_, i) => i !== index));
  };

  const handleAttachmentFileChange = (index: number, file: File | null) => {
    const newItems = [...attachmentItems];
    newItems[index].file = file;
    setAttachmentItems(newItems);
  };

  const handleAttachmentDescriptionChange = (index: number, description: string) => {
    const newItems = [...attachmentItems];
    newItems[index].description = description;
    setAttachmentItems(newItems);
  };

  useEffect(() => {
    if (file && previewColumns.length > 0 && previewItems.length > 0 && !previewError && previewIsValid) {
      setCanSubmit(true)
    } else {
      setCanSubmit(false)
    }
  }, [file, previewColumns, previewItems, previewError, previewIsValid])

  const handleSubmit = async (values: any) => {
    if (!file || !canSubmit) {
      if (!previewIsValid) {
        setPreviewModalOpen(true);
        message.error("Please complete the mandatory fields listed in the preview before submitting!");
        return;
      }

      message.error("File must be previewed and valid before submit!");
      return;
    }

    const isConfirmed = await confirmSapImportSubmit("Supplier");
    if (!isConfirmed) {
      return;
    }

    setLoading(true);
    try {
      // Extract files and descriptions from attachmentItems
      const attachments = attachmentItems
        .filter(item => item.file !== null)
        .map(item => item.file );
      
      const descriptions = attachmentItems
        .filter(item => item.file !== null)
        .map(item => item.description || "");
      
      // Join descriptions with comma
      const attachmentsDescription = descriptions.join(",");

      await createSapSupplier({
        employee_id: values.employee_id,
        fullname: values.fullname,
        level: values.level,
        department: values.department,
        type_of_request: values.type_of_request,
        note: values.note,
        file,
        attachments,
        attachments_description: attachmentsDescription,
      });
      message.success("Supplier created successfully!");
      form.resetFields();
      setFile(null);
      setAttachmentItems([]);
      setPreviewColumns([]);
      setPreviewItems([]);
      setPreviewModalOpen(false);
      navigate("/sapspl/history");
    } catch (err: any) {
      const validationState = extractSapImportValidationFromError(err);
      if (validationState.validationErrors.length) {
        setPreviewIsValid(validationState.isValid);
        setPreviewRequiredFields(validationState.requiredFields);
        setPreviewValidationErrors(validationState.validationErrors);
        setPreviewModalOpen(true);
        message.error("Please complete the mandatory fields listed in the preview before submitting!");
      } else {
        message.error(err?.response?.data?.message || "Failed to create supplier");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    form.setFieldsValue({
      employee_id: userData.employee_id || "",
      fullname: userData.fullname || "",
      level: userData.job_level || "",
      department: userData.organization_name || "",
    });
  }, [userData, form]);

  return (
    <TempLayout title="SAP - Create new Supplier" navNavigateUrl="/sapspl/history">
      <Card>
        <Spin spinning={loading}>
          <SapSupplierForm
            isEditMode={false}
            form={form}
            file={file}
            attachmentItems={attachmentItems}
            loading={loading}
            canSubmit={canSubmit}
            onFileChange={handleFileChange}
            onAddAttachment={handleAddAttachment}
            onRemoveAttachment={handleRemoveAttachment}
            onAttachmentFileChange={handleAttachmentFileChange}
            onAttachmentDescriptionChange={handleAttachmentDescriptionChange}
            onFinish={handleSubmit}
            previewModalOpen={previewModalOpen}
            previewLoading={previewLoading}
            previewError={previewError}
            previewColumns={previewColumns}
            previewItems={previewItems}
            previewIsValid={previewIsValid}
            previewRequiredFields={previewRequiredFields}
            previewValidationErrors={previewValidationErrors}
            setPreviewModalOpen={setPreviewModalOpen}
          />
        </Spin>
      </Card>
    </TempLayout>
  )
}

export default SapSuppliersAdd

